import axios from 'axios';
import { store } from '../Redux/Store';
import { apiUrl } from '../Utils/Environment';
import showToast from '../Shared/Utils/ToastNotification';
import { hideLoader, showLoader, clearAllLoaders } from '../Shared/Slice/LoaderSlice';
import { userlogout, acquireLogoutLock } from '../Shared/Slice/AuthSlice';
import CryptoJS from 'crypto-js';

// =======================
// 🔐 Crypto Configuration
// =======================
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;
const ADD_CRYPTO = process.env.REACT_APP_ADD_CRYPTO === 'true';

const isCryptoEnabled = () => ADD_CRYPTO;

const encryptData = (data) => {
  if (!isCryptoEnabled()) return data;
  try {
    return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Encryption failed');
  }
};

const decryptData = (ciphertext) => {
  if (!isCryptoEnabled()) return ciphertext;

  // Add validation
  if (!ciphertext || typeof ciphertext !== 'string') {
    console.error('Invalid ciphertext provided to decryptData:', ciphertext);
    return ciphertext;
  }

  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);

    // Check if decryption produced valid bytes
    if (!bytes || bytes.sigBytes <= 0) {
      console.error('Decryption produced empty or invalid bytes');
      throw new Error('Decryption produced invalid result');
    }

    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);

    // Check if the decrypted string is valid
    if (!decryptedString) {
      console.error('Decrypted string is empty or invalid');
      throw new Error('Decrypted string is empty');
    }

    const decryptedData = JSON.parse(decryptedString);
    return decryptedData;
  } catch (error) {
    console.error('Decryption failed:', error);
    console.error('Ciphertext that failed:', ciphertext);
    throw new Error('Decryption failed');
  }
};

// =======================
// 🧩 Axios Instance Setup
// =======================
const axiosInstance = axios.create({
  baseURL: apiUrl,
});

// =======================
// 🧾 Request Interceptor
// =======================
axiosInstance.interceptors.request.use(
  (config) => {
    const publicEndpoints = ['/auth/register', '/auth/login'];

    // Attach token if endpoint is private
    if (!publicEndpoints.some((ep) => config.url.includes(ep))) {
      const token = sessionStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Encrypt request data if crypto is enabled and data exists
    // Encrypt request data if crypto is enabled BUT NOT for FormData
    if (config.data && isCryptoEnabled() && !(config.data instanceof FormData)) {
      config.data = { encrypted_data: encryptData(config.data) };
      config.headers['X-Encrypt-Response'] = 'true';
    }

    // Show loader if configured
    if (config.loaderKey !== false) {
      store.dispatch(showLoader(config.loaderKey || true));
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// =======================
// 📨 Response Interceptor
// =======================
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.config.loaderKey !== false) {
      store.dispatch(hideLoader(response.config.loaderKey || true));
    }

    // Decrypt response data if applicable
    if (response.data && isCryptoEnabled()) {
      // Check if response is encrypted (backend format: {encrypted_data: "...", encrypted: true})
      if (response.data.encrypted === true && response.data.encrypted_data) {
        try {
          response.data = decryptData(response.data.encrypted_data);
        } catch (error) {
          console.error('Failed to decrypt structured response:', error);
          // Don't throw - return original data so app doesn't break
          console.warn('Returning original encrypted response due to decryption failure');
        }
      }
      // Handle legacy string responses (if any)
      else if (typeof response.data === 'string' && response.data.startsWith('U2FsdGVkX1')) {
        try {
          response.data = decryptData(response.data);
        } catch (error) {
          console.error('Failed to decrypt string response:', error);
          // Don't throw - return original data
        }
      }
    }

    return response;
  },
  async (error) => {
    const { response, config } = error;

    // Always hide loader
    if (config?.loaderKey !== false) {
      store.dispatch(hideLoader(config.loaderKey || true));
    }

    // Handle unauthorized
    if (response?.status === 401 && !config?._retry) {
      // Don't redirect for login endpoint - let the login component handle 401 errors
      if (config?.url?.includes('/api/accounts/login/')) {
        return Promise.reject(error);
      }
      try {
        config._retry = true;

        // If currently impersonating, do NOT try to refresh (we have no refresh token).
        const isImpersonating = sessionStorage.getItem('impersonating') === 'true';
        if (isImpersonating) {
          const originalToken = sessionStorage.getItem('original_admin_token');
          const originalRefresh = sessionStorage.getItem('original_admin_refresh_token');

          if (originalToken) {
            // restore original admin tokens
            sessionStorage.setItem('auth_token', originalToken);
            sessionStorage.setItem('access_token', originalToken);
            if (originalRefresh) sessionStorage.setItem('refresh_token', originalRefresh);

            // clear impersonation flags
            sessionStorage.removeItem('impersonating');
            sessionStorage.removeItem('impersonator_id');

            // Update header and retry once as admin
            config.headers = config.headers || {};
            config.headers['Authorization'] = `Bearer ${originalToken}`;
            showToast('info', 'Impersonation session expired. Restored admin session.');
            return axiosInstance(config);
          }
          // If no original token, fall through to logout
          throw new Error('Impersonation expired and no original admin token found');
        }

        // Normal refresh flow
        const refreshToken = sessionStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('Missing refresh token');
        }

        const refreshBase = (apiUrl || '').replace(/\/$/, '');
        const refreshResponse = await axios.post(`${refreshBase}/api/accounts/refresh/`, {
          refresh: refreshToken,
        });

        const newAccessToken = refreshResponse?.data?.access;
        if (!newAccessToken) {
          throw new Error('No access token in refresh response');
        }

        sessionStorage.setItem('access_token', newAccessToken);
        sessionStorage.setItem('auth_token', newAccessToken);

        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${newAccessToken}`;

        return axiosInstance(config);
      } catch (refreshError) {
        store.dispatch(clearAllLoaders());
        if (acquireLogoutLock()) {
          store.dispatch(userlogout());
          showToast(
            'error',
            response?.data?.message || response?.data?.error || response?.data?.detail || 'Unauthorized or session expired. Logging out...'
          );
          window.location.href = '/';
        }
        return Promise.reject(refreshError);
      }
    } else if (response?.status >= 500) {
      showToast('error', 'Server error. Please try again later.');
    } else if (response) {
      // Don't show automatic error toast for login endpoint - let the login component handle it
      if (config?.url?.includes('/api/accounts/login/')) {
        // Let the login component handle the error display
        return Promise.reject(error);
      }
      if (config?.url?.includes('/data/get/')) {
        return Promise.reject(error);
      }
      // Best-effort side fetch: the patient-orders page merges supplement orders
      // with the Shopify list and already degrades gracefully, so a failure here
      // shouldn't toast over a page that rendered fine.
      if (config?.url?.includes('/supplements/patients/') && config?.url?.includes('/orders/')) {
        return Promise.reject(error);
      }
      showToast('error', response?.data?.message || response?.data?.error || response?.data?.detail || 'An unexpected error occurred.');
    }

    return Promise.reject(error);
  }
);

export { axiosInstance, isCryptoEnabled, encryptData, decryptData };
